import { useTranslation } from 'react-i18next';
import SubscriptionEditor from './SubscriptionEditor';
import { AppContext } from '../../services/AppContext';
import { ModalContext } from '../../modal/ModalContext';
import MultiButtons from '../../components/global/MultiButtons';
import { React, Fragment, useState, useContext, useEffect } from 'react';
import { DeleteConfirmationModal } from '../../modal/global/DeleteConfirmationModal';

const Subscriptions = () => {
    const { t } = useTranslation();
    const { showModal } = useContext(ModalContext);
    const { apiClient, user, notify } = useContext(AppContext);
    const [subscriptionData, setSubscriptionData] = useState([]);
    const [filter, setFilter] = useState('');
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const take = 20;

    const handleAddSubscription = async () => {
        const created = await showModal(<SubscriptionEditor />);

        if (created) fetchData();
    };

    const handleEditSubscription = async (subscription) => {
        const updated = await showModal(<SubscriptionEditor subscription={subscription} />);

        if (updated) fetchData();
    };

    const handleDelete = async (subscription) => {
        const shouldDelete = await showModal(
            <DeleteConfirmationModal subject={`${subscription.owner}'s subscription`} />
        );

        if (shouldDelete) {
            await apiClient.deleteSubscription(subscription.id);
            fetchData();
            notify(t('pages.subscriptions.notifications.deleted'), 'success');
        }
    };

    const fetchData = async (filter) => {
        filter = filter ?? {};
        const data = await apiClient.getSubscriptions(
            {
                includeSubscriptionsWithNoClassesLeft: true,
                skip,
                take,
                ...filter
            },
            false
        );

        if (!data.hasErrors) {
            for (const sub of data) {
                sub.barWidth = 0;
            }

            setTimeout(() => {
                for (const sub of data) {
                    sub.barWidth = sub.classesLeft / sub.total;
                }

                setSubscriptionData((prev) => [...prev]);
            }, 100);

            setSubscriptionData((prev) => (skip === 0 ? data : [...prev, ...data]));
            setHasMore(data?.length === take);
        }
    };

    useEffect(() => {
        if (user.role === 'Admin')
            fetchData({
                owner: filter,
            });
    }, [filter, skip]);

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
        setSkip(0);
    };

    const handleScroll = () => {
        const threshold = 0.5;

        if (
            document.documentElement.scrollHeight -
            (window.innerHeight + document.documentElement.scrollTop) >
            threshold ||
            !hasMore
        ) {
            return;
        }

        setSkip((prev) => prev + take);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    });

    return user.role === 'Admin' ? (
        <div>
            <div className="filter-container mg-b">
                <input
                    type="text"
                    placeholder={t('pages.subscriptions.placeholders.filter')}
                    value={filter}
                    onChange={handleFilterChange}
                    className="filter-input"
                />

                <div className="mg-l-1">
                    <MultiButtons>
                        <button onClick={handleAddSubscription}>
                            {t('pages.subscriptions.actions.add')}
                        </button>
                    </MultiButtons>
                </div>
            </div>

            <div className="grid-container-3 mg-t-1">
                <div className="grid-header">
                    <div className="container">{t('pages.subscriptions.headers.owner')}</div>
                </div>
                <div className="grid-header">
                    <div className="container">{t('pages.subscriptions.headers.left')}</div>
                </div>
                <div className="grid-header">
                    <div className="container"></div>
                </div>
                {subscriptionData?.map((sub, index) => (
                    <Fragment key={index}>
                        <a href={`customer/${sub.ownerId}`}>
                            <div className="grid-item">
                                <div className="container">{sub.owner}</div>
                            </div>
                        </a>

                        <a href={`subscription/${sub.id}`}>
                            <div className="grid-left-item">
                                <div
                                    className="progress-background"
                                    style={{ width: `${sub.barWidth * 100}%` }}
                                />
                                <div className="left-container">
                                    <div>{sub.classesLeft}</div>
                                </div>
                            </div>
                        </a>

                        <MultiButtons>
                            <button
                                className="background-warning width-100p"
                                onClick={() => handleEditSubscription(sub)}
                            >
                                {t('pages.subscriptions.actions.edit')}
                            </button>
                            <button
                                className="background-danger width-100p"
                                onClick={() => handleDelete(sub)}
                            >
                                {t('pages.subscriptions.actions.delete')}
                            </button>
                        </MultiButtons>
                    </Fragment>
                ))}
            </div>
        </div>
    ) : user.role === 'Manager' ? (
        <div>{t('pages.subscriptions.messages.manager')}</div>
    ) : (
        <div>{t('pages.subscriptions.messages.customer')}</div>
    );
};

export default Subscriptions;